import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Loader2, Download, Check, Calendar, FileText, MessageCircle, Phone } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EUR = (v) => `${(Number(v) || 0).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

export default function SammelrechnungSeite() {
  const { token } = useParams();
  const [d, setD] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/rechnungen/${token}`);
        setD(data);
      } catch (err) {
        setError(err.response?.data?.detail || "Diese Rechnung gibt es nicht — bitte den Link prüfen.");
      }
    })();
  }, [token]);

  if (error) {
    return (
      <div className="min-h-screen bg-[#FDF8F1] flex items-center justify-center px-6">
        <p className="font-serif text-2xl text-[#2B2320] text-center" data-testid="sammelrechnung-error">{error}</p>
      </div>
    );
  }
  if (!d) {
    return (
      <div className="min-h-screen bg-[#FDF8F1] flex items-center justify-center">
        <Loader2 className="animate-spin text-[#BA6D52]" size={30} />
      </div>
    );
  }

  return (
    <div data-testid="page-sammelrechnung" className="min-h-screen bg-[#FDF8F1] text-[#2B2320]">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url('/event-bg-teller.jpg')" }} />
        <div className="absolute inset-0" style={{ backgroundColor: "rgba(20,18,14,0.78)" }} />
        <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-b from-transparent to-[#FDF8F1]" />
        <div className="relative max-w-3xl mx-auto px-6 pt-10 pb-24 text-center">
          <img src="/logo.png" alt="Lukas Stutz"
            className="h-16 w-16 md:h-20 md:w-20 mx-auto rounded-full bg-[#FAF8F3]/90 p-1.5" />
          <p className="text-[10px] uppercase tracking-[0.35em] text-[#E2C4B2] mt-4">
            Lukas Stutz · Mietkoch für Gastronomie &amp; Events
          </p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-[#FFFBF4] mt-10 drop-shadow-[0_3px_16px_rgba(0,0,0,0.9)]"
            data-testid="sammelrechnung-titel">
            {d.nummer ? `Rechnung ${d.nummer}` : "Ihre Rechnung"}
          </h1>
          <p className="text-base md:text-lg text-[#F2E7DA] mt-6 font-light leading-relaxed drop-shadow-[0_2px_10px_rgba(0,0,0,0.85)]">
            Für {d.betrieb}
            {d.leistungszeitraum ? ` · Einsätze ${d.leistungszeitraum}` : d.einsatzdatum_de ? ` · Einsatz am ${d.einsatzdatum_de}` : ""}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-7 gap-y-3 mt-9 text-sm text-[#EADFD2]">
            {d.rechnungsdatum_de && (
              <span className="inline-flex items-center gap-2">
                <Calendar size={14} className="text-[#D99A5B]" /> Rechnungsdatum {d.rechnungsdatum_de}
              </span>
            )}
            {d.nachweise > 0 && (
              <span className="inline-flex items-center gap-2" data-testid="sammelrechnung-nachweise">
                <FileText size={14} className="text-[#D99A5B]" /> {d.nachweise} Stundennachweise enthalten
              </span>
            )}
          </div>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-5 sm:px-6 -mt-14 relative pb-6">
        <div className="bg-[#FFFDF8] border border-[#E6D8C2] shadow-[0_26px_60px_-38px_rgba(120,80,48,0.55)] px-6 sm:px-10 py-10">
          {d.test && (
            <p className="mb-7 text-sm text-[#8B7A68] border border-[#EBDFCB] bg-[#FAF8F3] px-4 py-3"
              data-testid="sammelrechnung-testhinweis">
              Testansicht — so sieht die Seite für den Kunden aus. Dies ist keine echte Rechnung.
            </p>
          )}
          {d.storniert && (
            <p className="mb-7 text-sm text-[#9C4F31] border border-[#E8C9BB] bg-[#FAF1EC] px-4 py-3"
              data-testid="sammelrechnung-storniert">
              Diese Rechnung wurde storniert und ist nicht mehr zu zahlen.
            </p>
          )}
          {d.in_arbeit && !d.storniert && (
            <p className="mb-7 text-sm text-[#8B7A68] border border-[#EBDFCB] bg-[#FAF8F3] px-4 py-3">
              Die Rechnung wird gerade erstellt — bitte in wenigen Minuten erneut laden.
            </p>
          )}

          <p className="text-[10px] uppercase tracking-[0.3em] text-[#BA6D52]">Positionen</p>
          <div className="mt-5 space-y-3 text-sm" data-testid="sammelrechnung-positionen">
            {d.positionen.map((p, i) => (
              <div key={i} className="flex justify-between gap-4 pb-3 border-b border-[#F0E6D6]">
                <span className="text-[#5A4D43]">
                  {p.name}
                  <span className="block text-xs text-[#8B7A68] mt-1">
                    {p.beschreibung}
                    {p.menge ? `${p.beschreibung ? " · " : ""}${String(p.menge).replace(".", ",")} ${p.einheit} × ${EUR(p.einzelpreis)}` : ""}
                  </span>
                </span>
                <span className="tabular-nums whitespace-nowrap">{EUR(p.summe)}</span>
              </div>
            ))}
          </div>

          <div className="mt-7 space-y-2.5 text-sm" data-testid="sammelrechnung-summen">
            <div className="flex justify-between gap-4">
              <span className="text-[#8B7A68]">Summe netto</span>
              <span className="tabular-nums">{EUR(d.netto)}</span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="text-[#8B7A68]">MwSt {d.mwst_prozent} %</span>
              <span className="tabular-nums">{EUR(d.mwst)}</span>
            </div>
            <div className="flex justify-between gap-4 pt-3 border-t border-[#EBDFCB] font-medium">
              <span>{d.hat_anzahlung ? "Gesamtrechnung brutto" : "Gesamtbetrag brutto"}</span>
              <span className="tabular-nums" data-testid="sammelrechnung-brutto">{EUR(d.brutto)}</span>
            </div>
            {d.hat_anzahlung && (
              <>
                <div className="flex justify-between gap-4 pt-2">
                  <span className="text-[#8B7A68]">abzgl. bereits berechnete Anzahlung netto</span>
                  <span className="tabular-nums">−{EUR(d.anzahlung_netto)}</span>
                </div>
                <div className="flex justify-between gap-4">
                  <span className="text-[#8B7A68]">abzgl. MwSt {d.mwst_prozent} % auf die Anzahlung</span>
                  <span className="tabular-nums">−{EUR(d.anzahlung_mwst)}</span>
                </div>
                <div className="flex justify-between gap-4 font-medium">
                  <span>Anzahlung brutto</span>
                  <span className="tabular-nums">−{EUR(d.anzahlung_brutto)}</span>
                </div>
              </>
            )}
            <div className="flex justify-between gap-4 pt-4 border-t border-[#D9C7AE] text-base font-medium">
              <span>{d.hat_anzahlung ? "Noch zu zahlen" : "Zahlbetrag"}</span>
              <span className="tabular-nums" data-testid="sammelrechnung-zu-zahlen">{EUR(d.zu_zahlen)}</span>
            </div>
            {d.hat_anzahlung && (
              <p className="text-xs text-[#8B7A68] text-right" data-testid="sammelrechnung-zu-zahlen-aufschluesselung">
                davon netto {EUR(d.zu_zahlen_netto)} · MwSt {d.mwst_prozent} % {EUR(d.zu_zahlen_mwst)}
              </p>
            )}
          </div>

          {d.bezahlt_am_de ? (
            <p className="mt-7 inline-flex items-center gap-2 text-sm text-[#4F6B3E] border border-[#CFE0C3] bg-[#F3F7EF] px-4 py-2.5"
              data-testid="sammelrechnung-bezahlt">
              <Check size={15} /> Bezahlt am {d.bezahlt_am_de} — vielen Dank.
            </p>
          ) : !d.storniert && (
            <div className="mt-7 border border-[#EBDFCB] bg-[#FAF5F1] px-5 py-5 text-sm" data-testid="sammelrechnung-zahlung">
              <p className="text-[10px] uppercase tracking-[0.25em] text-[#BA6D52]">Zahlung</p>
              {d.zahlbar_bis_de && (
                <p className="mt-3 text-[#5A4D43]">Zahlbar bis <b>{d.zahlbar_bis_de}</b> ({d.zahlungsziel_tage} Tage netto)</p>
              )}
              {d.iban && (
                <p className="mt-2 text-[#5A4D43]">
                  IBAN <span className="font-mono tracking-wide">{d.iban}</span>{d.bank ? ` · ${d.bank}` : ""}
                </p>
              )}
              {d.verwendungszweck && (
                <p className="mt-2 text-[#5A4D43]">Verwendungszweck <b>{d.verwendungszweck}</b></p>
              )}
            </div>
          )}

          {!d.storniert && (
            <a href={`${API}/rechnungen/${token}/pdf`} data-testid="sammelrechnung-download"
              className="mt-8 w-full inline-flex items-center justify-center gap-2.5 bg-[#BA6D52] hover:bg-[#9C4F31] text-white px-8 py-4 text-xs uppercase tracking-[0.2em] transition-colors">
              <Download size={15} /> Rechnung {d.nachweise > 0 ? "mit Stundennachweisen " : ""}als PDF
            </a>
          )}

          {d.hinweis && (
            <p className="text-sm text-[#6B5C50] mt-7 font-light leading-relaxed whitespace-pre-line"
              data-testid="sammelrechnung-hinweis">{d.hinweis}</p>
          )}
        </div>
      </section>

      <section className="relative overflow-hidden text-[#FAF3E8] mt-10">
        <div className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url('/event-bg-gericht.jpg')" }} />
        <div className="absolute inset-0" style={{ backgroundColor: "rgba(14,13,10,0.74)" }} />
        <div className="relative max-w-3xl mx-auto px-6 py-16 text-center">
          <h2 className="font-serif text-2xl md:text-3xl font-light">Eine Frage zur Rechnung?</h2>
          <p className="text-sm text-[#A89D91] mt-3 font-light">
            Kurz schreiben oder anrufen — ich klär das sofort.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3 mt-8">
            <a href={`https://wa.me/${d.phone_intl}?text=${encodeURIComponent(`Guten Tag Herr Stutz, eine Frage zur Rechnung ${d.nummer}:`)}`}
              target="_blank" rel="noreferrer" data-testid="sammelrechnung-whatsapp"
              className="inline-flex items-center gap-2 border border-emerald-700/60 text-emerald-300 hover:bg-emerald-900/20 px-6 py-3 text-xs uppercase tracking-[0.2em] transition-colors">
              <MessageCircle size={15} /> WhatsApp
            </a>
            <a href={`tel:+${d.phone_intl}`} data-testid="sammelrechnung-call"
              className="inline-flex items-center gap-2 border border-[#4A433B] text-[#FAF3E8] hover:border-[#BA6D52] px-6 py-3 text-xs uppercase tracking-[0.2em] transition-colors">
              <Phone size={15} /> Anrufen
            </a>
          </div>
          <p className="text-[11px] text-[#6E655C] mt-12">
            Lukas Stutz · Mietkoch für Gastronomie &amp; Events · stutz-cuisine.com
          </p>
        </div>
      </section>
    </div>
  );
}
